import React, { memo, useCallback, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { createUseStyles } from 'react-jss';
import moment from 'moment-timezone';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import momentPlugin from '@fullcalendar/moment';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import FullCalendar from '@fullcalendar/react';
import momentTimezonePlugin from '@fullcalendar/moment-timezone';
import {
  DateSelectArg,
  EventChangeArg,
  EventClickArg,
  EventSourceInput,
} from '@fullcalendar/core';

import { Theme } from '@app/theme';
import { Button, SquareButton, useModalState } from '@app/core';
import { TimezoneSelect } from '@app/form';
import { ModeratedTaskSetting, useMe } from '@app/api';

import './calendar-vars.css';
import { DayHeader } from './DayHeader';
import {
  EventType,
  useGetEventClassNames,
} from './hooks/useGetEventClassNames';
import { calendarLocales } from './utils/calendarLocales';
import { useDisabledBackgroundEvents } from './hooks/useDisabledBackgroundEvents';
import { EventPopover, PopoverInfo } from './EventPopover';
import { CalendarEventContent } from './CalendarEventContent';
import { CalendarGroupContent } from './CalendarGroupContent';

const useStyles = createUseStyles((theme: Theme) => ({
  slotLabel: {
    ...theme.fonts.calendarText,
    color: theme.colors.inkTertiary,
    fontWeight: 700,
    '& .fc-timegrid-slot-label-frame': {
      textAlign: 'left',
    },

    '& .fc-timegrid-slot-label-cushion': {
      paddingRight: theme.marginBase,
      paddingLeft: theme.marginBase,
      minWidth: theme.marginBase * (5 + 2),
    },
  },
  calendar: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 600,
    '& .fc': {
      flex: 1,
    },
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.marginBase,
    marginBottom: theme.marginBase * 2,
    position: 'relative',
    zIndex: 10,
  },
  center: {
    display: 'flex',
    alignItems: 'center',
    flex: 1,
  },
  timezone: {
    width: 260,
  },
  settingIcon: {
    fontSize: theme.iconSize.large,
    marginRight: theme.marginBase,
  },
}));

const PureCalendar = memo(FullCalendar);

const orderFunction = (a: any, b: any) => {
  const atype: EventType = a.type || EventType.EVENT;
  const btype: EventType = b.type || EventType.EVENT;
  const types = [
    EventType.AVAILABILITY,
    EventType.APPOINTMENT,
    EventType.EVENT,
  ];
  if (atype === EventType.EVENT && btype === EventType.EVENT) {
    if (a.resourceId && b.resourceId) {
      return a.resourceId.localeCompare(b.resourceId);
    }
    return a.duration - b.duration;
  }
  return types.indexOf(atype) - types.indexOf(btype);
};

export interface BaseEvent {
  id: string;
  start: Date;
  end: Date;
  type?: EventType;
  title?: string;
  editable?: boolean;
  resourceId?: string;
  attendees?: string[];
}

interface Props {
  eventSources?: EventSourceInput[];
  onCreateEvent?: (event: BaseEvent) => any;
  onEditEvent?: (event: BaseEvent) => any;
  onDeleteEvent?: (event: BaseEvent) => any;
  onSettingsClick: () => any;
  settings?: ModeratedTaskSetting;
  minDuration?: number;
}

const plugins = [
  timeGridPlugin,
  momentPlugin,
  interactionPlugin,
  momentTimezonePlugin,
];

const views = {
  timeGrid: {
    type: 'timeGridWeek',
  },
};
const validRange = { start: moment().toISOString(true) };

const slotLabelFormat = {
  hour: '2-digit' as '2-digit',
  minute: '2-digit' as '2-digit',
  omitZeroMinute: false,
};

const headerToolbar = {
  left: '',
  center: '',
  right: '',
};

export const Calendar = ({
  eventSources = [],
  onCreateEvent,
  onEditEvent,
  onDeleteEvent,
  onSettingsClick,
  settings,
  minDuration = 30,
}: Props) => {
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  const calendarRef = useRef<FullCalendar | null>(null);
  const { data: me } = useMe();

  const getEventClassNames = useGetEventClassNames();
  const [timezoneState, setTimezone] = useState<string | null>(null);
  const timezone = timezoneState || me?.timezone || moment.tz.guess();
  const disabledBackgroundEvents = useDisabledBackgroundEvents(timezone);
  const [popoverInfo, setPopoverInfo, closePopover] = useModalState<
    PopoverInfo | undefined
  >(undefined);
  const isGroup = settings?.groupMeeting;

  const renderDayHeader = useCallback(
    (info: any) => <DayHeader info={info} />,
    [],
  );
  const renderEventContent = useCallback(
    (eventInfo: any) => {
      if (eventInfo.event.extendedProps.type === EventType.AVAILABILITY) {
        return (
          <CalendarEventContent eventInfo={eventInfo} settings={settings} />
        );
      }
      if (
        eventInfo.event.extendedProps.type === EventType.APPOINTMENT &&
        isGroup
      ) {
        return (
          <CalendarGroupContent eventInfo={eventInfo} settings={settings} />
        );
      }
      return true;
    },
    [settings, isGroup],
  );

  const handleSelection = useCallback(
    (selection: DateSelectArg) => {
      if (!onCreateEvent) {
        return;
      }

      const diff = moment(selection.end).diff(selection.start, 'minutes');
      const id = uuid();
      const { jsEvent } = selection;
      if ((diff < minDuration || diff === 30) && jsEvent && !isGroup) {
        setPopoverInfo({
          eventId: id,
          coords: {
            x: jsEvent.x,
            y: jsEvent.y,
          },
        });
      }

      onCreateEvent({
        id,
        start: selection.start,
        end:
          diff >= minDuration && !isGroup
            ? selection.end
            : moment(selection.start).add(minDuration, 'minutes').toDate(),
        type: EventType.AVAILABILITY,
      });
    },
    [onCreateEvent, minDuration],
  );

  const handleChange = useCallback(
    (info: EventChangeArg) => {
      const { event } = info;
      if (!onEditEvent) {
        return;
      }
      const { start, end } = event;
      if (!start || !end) {
        return;
      }
      const diff = moment(end).diff(start, 'minutes');
      onEditEvent({
        id: event.id,
        start,
        end:
          diff >= minDuration && !isGroup
            ? end
            : moment(start).add(minDuration, 'minutes').toDate(),
        ...event.extendedProps,
      });
    },
    [onCreateEvent, minDuration, isGroup],
  );

  const handleEventClick = useCallback(
    (info: EventClickArg) => {
      const { start, end, extendedProps } = info.event;
      if (!start || !end) {
        return;
      }
      if (extendedProps.type !== EventType.AVAILABILITY) {
        return;
      }
      setPopoverInfo({
        eventId: info.event.id,
        coords: {
          x: info.jsEvent?.x,
          y: info.jsEvent?.y,
        },
      });
    },
    [setPopoverInfo],
  );

  const next = useCallback(() => {
    calendarRef.current?.getApi().next();
  }, [calendarRef]);
  const prev = useCallback(() => {
    calendarRef.current?.getApi().prev();
  }, [calendarRef]);
  const today = useCallback(() => {
    calendarRef.current?.getApi().today();
  }, [calendarRef]);

  const getNow = useCallback(
    () => moment.tz(timezone).toISOString(true),
    [timezone],
  );

  return (
    <div className={classes.calendar}>
      <div className={classes.header}>
        <Button small onClick={today} color="ink" variant="muted">
          {t('calendar.core.today')}
        </Button>
        <SquareButton icon="ri-arrow-left-s-line" onClick={prev} color="ink" />
        <SquareButton icon="ri-arrow-right-s-line" onClick={next} color="ink" />
        <TimezoneSelect
          value={timezone}
          onChange={setTimezone}
          smallSelect
          noMargin
          containerClass={classes.timezone}
        />
        <div className={classes.center} />

        <Button variant="muted" onClick={onSettingsClick} color="blue" small>
          <i
            className={classNames(classes.settingIcon, 'ri-settings-3-line')}
          />
          {t('calendar.settings.title')}
        </Button>
      </div>
      <PureCalendar
        key={timezone} // force re-render when timezone changes to update the now indicator
        plugins={plugins}
        initialView="timeGridWeek"
        ref={calendarRef}
        views={views}
        eventSources={[disabledBackgroundEvents, ...eventSources]}
        selectable
        editable
        eventConstraint={validRange}
        selectConstraint={validRange}
        select={handleSelection}
        eventClick={handleEventClick}
        eventChange={handleChange}
        eventClassNames={getEventClassNames}
        slotDuration="00:30:00"
        dayHeaderContent={renderDayHeader}
        slotLabelFormat={slotLabelFormat}
        slotLabelClassNames={classes.slotLabel}
        expandRows
        height="100%"
        weekends={settings ? settings.displayWeekendOnCalendar : settings}
        stickyHeaderDates
        headerToolbar={headerToolbar}
        eventOrder={orderFunction}
        eventContent={renderEventContent}
        nowIndicator
        now={getNow}
        allDayContent=""
        locale={i18n.language}
        locales={calendarLocales}
        scrollTime="08:00"
        timeZone={timezone}
        eventMinHeight={20}
        firstDay={1}
      />
      <EventPopover
        info={popoverInfo}
        eventSources={eventSources}
        onRequestClose={closePopover}
        onDeleteEvent={onDeleteEvent}
        onEditEvent={onEditEvent}
        timezone={timezone}
        minDuration={minDuration}
        fixedDuration={settings?.groupMeeting}
      />
    </div>
  );
};
